




































































import { defineComponent, PropType } from '@nuxtjs/composition-api'
import ClassifiedView from '~/components/car/classifieds/view/views/ClassifiedView.vue'
import ClassifiedViewShimmer from '~/components/car/classifieds/view/views/ClassifiedViewShimmer.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { resolveCategory } from '~/utils/category'
import DisabledOverlay from '~/components/car/classifieds/view/DisabledOverlay.vue'
import { USER_NS } from '~/store/modules/shared/user/state'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import CJsonld from '~/components/shared/configurable/seo/CJsonld.vue'
import ClassifiedService from '~/services/classified/ClassifiedService'
import PaidClassifiedService from '~/services/classified/paid/PaidClassifiedService'
import PageViewRecorder from '~/services/telemetry/PageViewRecorder'
import { CategoryId } from '~/models/category/types'
import { PARKING_NS } from '~/store/modules/shared/parking/state'
import {
  REMOVE_CLASSIFIED,
  TOGGLE_RECENT_PARKED
} from '~/store/modules/shared/parking/mutation-types'
import { OfferType } from '~/models/classified/types'
import SaleRequestActiveAlert from '~/components/car/classifieds/SaleRequestActiveAlert.vue'
import SaleRequestsOverview from '~/components/car/sale-requests/SaleRequestsOverview.vue'
import { defineVue3AsyncComponent } from '~/utils/nuxt3-migration'

const viewComponents = {
  [CategoryId.JOBS]: defineVue3AsyncComponent(() =>
    import('~/components/car/classifieds/view/views/JobsView.vue')
  ),
  [CategoryId.CLASSIFIEDS]: ClassifiedView,
  PlotView: defineVue3AsyncComponent(() =>
    import('~/components/plot/classifieds/view/PlotView.vue')
  ),
  [CategoryId.RENTALS]: defineVue3AsyncComponent(() =>
    import('~/components/car/classifieds/view/views/RentalView.vue')
  ),
  [CategoryId.WANTED_PARTS]: defineVue3AsyncComponent(() =>
    import('~/components/car/classifieds/view/views/WantedPartsView.vue')
  )
}
const shimmerComponents = {
  [CategoryId.JOBS]: defineVue3AsyncComponent(() =>
    import('../../../car/classifieds/view/views/JobsShimmer.vue')
  ),
  [CategoryId.CLASSIFIEDS]: ClassifiedViewShimmer,
  PlotViewShimmer: defineVue3AsyncComponent(() =>
    import('../../../plot/classifieds/view/PlotViewShimmer.vue')
  ),
  [CategoryId.RENTALS]: () => null
}
export default defineComponent({
  components: {
    Logs: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/view/admin/Logs.vue')
    ),
    GeolocationMap: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/view/admin/GeolocationMap.vue')
    ),
    Reports: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/view/admin/Reports.vue')
    ),
    NonPublicAlert: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/NonPublicAlert.vue')
    ),
    PaidClassifiedViewAlert: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/paid/PaidClassifiedViewAlert.vue')
    ),
    DealersOnlyAlert: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/view/DealersOnlyAlert.vue')
    ),
    PostDeleteActions: defineVue3AsyncComponent(() =>
      import(
        '~/components/car/classifieds/view/post-delete/PostDeleteActions.vue'
      )
    ),
    HiddenAlert: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/HiddenAlert.vue')
    ),
    ClassifiedView,
    DisabledOverlay,
    ClassifiedViewShimmer,
    CJsonld,
    SaleRequestActiveAlert,
    SaleRequestsOverview
  },
  props: {
    offertype: {
      type: String as PropType<OfferType>,
      default: ''
    }
  },
  computed: {
    ...mapState(CLASSIFIED_VIEW_NS, {
      debugShimmer: state => state.debugShimmer,
      classified: state => state.classified,
      seo: state => state.classified.seo,
      refs: state => state.refs,
      offerCount: state => state.offerCount,
      audits: state => state.audits
    }),
    ...mapGetters(USER_NS, {
      userIsAdmin: 'isAdmin',
      userIsSingle: 'isSingle'
    }),
    ...mapGetters(CLASSIFIED_VIEW_NS, {
      userIsAdminOrOwner: 'userIsAdminOrOwner',
      userIsOwner: 'isOwn',
      classifiedIsNonPublic: 'isNonPublic',
      classifiedIsHidden: 'isHidden',
      classifiedIsDeleted: 'isDeleted',
      classifiedIsDealersOnly: 'isDealersOnly',
      paidClassified: 'paidClassified'
    }),
    ViewComponent() {
      return (
        resolveCategory(viewComponents, this.classified.category_ids) ||
        ClassifiedView
      )
    },
    ShimmerComponent() {
      if (this.refs && this.refs.categoryIds) {
        return (
          resolveCategory(shimmerComponents, this.refs.categoryIds) ||
          ClassifiedViewShimmer
        )
      } else {
        return ClassifiedViewShimmer
      }
    },
    noPaymentProblems() {
      return (
        !this.paidClassified ||
        (this.paidClassified &&
          !(
            this.paidClassified.requiresPayment ||
            this.paidClassified.hasPaymentFailure
          ))
      )
    },
    activeSaleRequestStatus() {
      return this.classified.saleRequests?.activeSaleRequestStatus
    },
    classifiedIsHiddenDueToSaleRequest() {
      return ['completed', 'pending'].includes(this.activeSaleRequestStatus)
    },
    showSaleRequestAlert() {
      if (!this.activeSaleRequestStatus) {
        return false
      }

      return this.classifiedIsHiddenDueToSaleRequest
    },
    showSaleRequestsOverview() {
      return (
        this.userIsOwner && this.userIsSingle && this.activeSaleRequestStatus
      )
    },
    showNonPublicAlert() {
      return (
        this.userIsAdminOrOwner &&
        this.classifiedIsNonPublic &&
        this.noPaymentProblems
      )
    },
    showHiddenAlert() {
      // Hide the hidden classified alert if the classified is non-public and has a sale request
      if (this.classifiedIsNonPublic && this.activeSaleRequestStatus) {
        return false
      }

      return (
        this.classifiedIsHidden &&
        this.userIsAdminOrOwner &&
        !this.paidClassified &&
        !this.classifiedIsDeleted
      )
    },
    showDisabledOverlay() {
      if (this.userIsAdmin) {
        return false
      }

      if (this.userIsOwner && this.classifiedIsHiddenDueToSaleRequest) {
        return true
      }

      return this.classifiedIsDeleted
    },
    showDealersOnlyAlert() {
      return (
        !this.classifiedIsNonPublic &&
        !this.classifiedIsDeleted &&
        !this.classifiedIsHidden &&
        this.classifiedIsDealersOnly
      )
    }
  },
  mounted() {
    this.$store.subscribe(mutation => {
      if (
        mutation.type === `${PARKING_NS}/${REMOVE_CLASSIFIED}` ||
        mutation.type === `${PARKING_NS}/${TOGGLE_RECENT_PARKED}`
      ) {
        const status = mutation.payload.parked || false
        this.$store.dispatch('classifieds/view/setParked', status)
      }
    })

    const parkOpenCookie = this.$cookies.get('parking_open')

    const styleCookie = this.$cookies.get('parkingStyle')

    if (styleCookie && parkOpenCookie) {
      if (styleCookie === 'list') {
        this.showParkingModal()
      } else {
        this.hideParkingModal()
      }
    }
  },
  methods: {
    ...mapActions(CLASSIFIED_VIEW_NS, {
      performAction: 'performAction'
    }),
    showClassified() {
      this.performAction(() => {
        const classifiedService = this.$dep(ClassifiedService)
        return classifiedService.show(this.classified.id)
      })
    },
    async enablePaidClassified() {
      await this.performAction(() => {
        const paidClassifiedService = this.$dep(PaidClassifiedService)
        return paidClassifiedService.pay(this.classified.id)
      })
      return this.$store.dispatch(`${USER_NS}/loadUser`, {}, { root: true })
    },
    onViewMounted() {
      const pageViewRecorderService = this.$dep(PageViewRecorder)

      if (this.classified?.offers_enabled && this.offerCount === null) {
        this.$store.dispatch(`${CLASSIFIED_VIEW_NS}/fetchOfferCount`)
      }

      pageViewRecorderService.recordStatClassifiedPageView()
      pageViewRecorderService.handleSellerPageViewOfClassified(this.$route)
    },
    showParkingModal() {
      this.$store.dispatch('parking/show_parking')
    },
    hideParkingModal() {
      this.$store.dispatch('parking/hide_parking')
    }
  }
})
